<script setup lang="ts">
import type { SurpriseBag, OriginCoordinates, SurpriseBagTranslations } from '@pkg/types/surpriseBag';
import { logger } from '@pkg/logger';
import SurpriseBagCard from '@pkg/ui/components/ui/surpriseBagCard/SurpriseBagCardUi.vue';
import { getBagAvailability } from '@pkg/utilities/surprisebag';
import { ampli } from '@pkg/analytics-helper/src/ampli';
import { useGoogleMaps } from '~/app/composables/useGoogleMaps';

const props = defineProps<{
  address: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  surpriseBags?: SurpriseBag[];
  staticTranslations?: SurpriseBagTranslations;
  fullPage?: boolean;
}>();

const MAP_ID = '6b6b07db662d4d072d9e20f0';
const MAP_ZOOM = 15;

const mapRef = ref<HTMLElement>();
const selectedBagKey = ref<string | null>(null);
const activeMarkerElement = ref<HTMLElement | null>(null);
const { loadGoogleMaps } = useGoogleMaps();
const emit = defineEmits<{
  mapChanged: [payload: { center: { latitude: number; longitude: number; }; radiusKm: number; zoom?: number }];
  mapLoaded: [];
  mapError: [error: Error | string];
  openCardModal: [bag: SurpriseBag];
}>();

let mapInstance: any = null;
let markers: any[] = [];
let idleDebounce: number | null = null;
let mapFetchDebounce: number | null = null;
let lastMapQuery: { center: { latitude: number; longitude: number; }; radiusKm: number; zoom: number } | null = null;
let lastOpenCardModalTime: number | null = null;

type BagInfo = {
  displayName: string;
  coordinates: OriginCoordinates;
  address: string;
  logoUrl: string;
  bag: SurpriseBag;
};
const bags = computed<BagInfo[]>(() =>
  props.surpriseBags?.map(bag => ({
    displayName: bag.displayName,
    coordinates: bag.store.storeLocation.location as OriginCoordinates,
    address: bag.store.storeLocation.address.addressLine,
    logoUrl: bag.item.logoPicture.currentUrl,
    bag,
  })) || [],
);

const selectedBag = computed(() =>
  bags.value.find(b => b.displayName === selectedBagKey.value)?.bag,
);
const closeOverlay = () => {
  if (activeMarkerElement.value) {
    activeMarkerElement.value.style.border = '';
    activeMarkerElement.value = null;
  }
  selectedBagKey.value = null;
  lastOpenCardModalTime = null;
};

const handleMarkerClick = (bag: BagInfo, e: Event) => {
  e.preventDefault();
  e.stopPropagation();

  ampli.track({
    event_type: 'map_view_item_browse',
    user_properties: {
      item_id: bag?.bag?.item?.itemId || '',
      store_id: bag?.bag?.store?.storeId || '',
      sold_out: getBagAvailability(bag?.bag) || bag?.bag?.itemsAvailable === 0,
    },
  });
  const target = e.target as HTMLElement;
  // Remove active class from previous marker
  if (activeMarkerElement.value && activeMarkerElement.value !== target) {
    activeMarkerElement.value.style.border = '';
  }
  const wasSelected = selectedBagKey.value === bag.displayName;
  selectedBagKey.value = wasSelected ? null : bag.displayName;
  if (selectedBagKey.value === bag.displayName) {
    target.style.border = '4px solid var(--theme-heading-color-app)';
    activeMarkerElement.value = target;
    lastOpenCardModalTime = Date.now();
    emit('openCardModal', bag.bag);
    if (mapInstance) {
      mapInstance.panTo({
        lat: bag.coordinates.latitude,
        lng: bag.coordinates.longitude,
      });
    }
  } else if (activeMarkerElement.value === target) {
    target.style.border = '';
    activeMarkerElement.value = null;
  }
};

const handleMapClick = () => {
  closeOverlay();
};

const handleItemClicked = (bag: SurpriseBag) => {
  ampli.track({
    event_type: 'map_view_item_click',
    user_properties: {
      item_id: bag?.item?.itemId || '',
      store_id: bag?.store?.storeId || '',
      sold_out: getBagAvailability(bag) || false,
    },
  });
};
const createMarkerElement = (bag: BagInfo): HTMLDivElement => {
  const container = document.createElement('div');
  const img = document.createElement('img');

  img.src = bag.logoUrl;
  img.alt = bag.displayName;
  img.className = 'custom-marker';
  img.style.cssText = `
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    object-fit: cover;
    cursor: pointer;
    transition: transform 0.2s ease;
    display: block;
  `;

  img.addEventListener('click', (e) => {
    e.stopPropagation();
    handleMarkerClick(bag, e);
  });

  img.addEventListener('mouseenter', () => {
    img.style.transform = 'scale(1.2)';
  });

  img.addEventListener('mouseleave', () => {
    img.style.transform = 'scale(1)';
  });

  container.className = 'custom-marker-container';
  container.appendChild(img);
  return container;
};
const clearMarkers = () => {
  if (activeMarkerElement.value) {
    activeMarkerElement.value.style.border = '';
    activeMarkerElement.value = null;
  }
  markers.forEach((marker) => {
    marker.map = null;
  });
  markers = [];
};

const addMarkersToMap = async() => {
  if (!mapInstance || !bags.value.length) {
    return;
  }

  try {
    // @ts-ignore - Google Maps types
    const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

    clearMarkers();

    bags.value.forEach((bag) => {
      const marker = new AdvancedMarkerElement({
        position: {
          lat: bag.coordinates.latitude,
          lng: bag.coordinates.longitude,
        },
        map: mapInstance,
        title: bag.displayName,
      });
      marker.content = createMarkerElement(bag);
      markers.push(marker);
    });
  } catch (error) {
    logger.error('Failed to add markers to map', error);
    emit('mapError', error instanceof Error ? error : new Error(String(error)));
  }
};
const initializeMap = async() => {
  if (!mapRef.value || !bags.value.length) {
    return;
  }

  try {
    // @ts-ignore - Google Maps types
    const { Map } = await google.maps.importLibrary('maps');

    const firstBag = bags.value[0];
    mapInstance = new Map(mapRef.value, {
      center: {
        lat: firstBag.coordinates.latitude,
        lng: firstBag.coordinates.longitude,
      },
      zoom: MAP_ZOOM,
      mapId: MAP_ID,
    });

    mapInstance.addListener('click', handleMapClick);
    // Emit changes when user pans/zooms the map
    mapInstance.addListener('idle', () => {
      if (!mapInstance) {
        return;
      }
      if (idleDebounce) {
        window.clearTimeout(idleDebounce);
      }
      idleDebounce = window.setTimeout(() => {
        try {
          const center = mapInstance.getCenter();
          const bounds = mapInstance.getBounds();
          if (!center || !bounds) {
            return;
          }
          const ne = bounds.getNorthEast();
          // approximate radius from center to north-east bound
          const radiusKm = haversineKm(center.lat(), center.lng(), ne.lat(), ne.lng());
          const payload = {
            center: { latitude: center.lat(), longitude: center.lng() },
            radiusKm,
            zoom: mapInstance.getZoom(),
          };

          if (!shouldEmitMapChange(payload)) {
            return;
          }

          lastMapQuery = { center: payload.center, radiusKm: payload.radiusKm, zoom: payload.zoom || 0 };

          if (mapFetchDebounce) {
            window.clearTimeout(mapFetchDebounce);
          }
          mapFetchDebounce = window.setTimeout(() => {
            const timeSinceOpenCardModal = lastOpenCardModalTime ? Date.now() - lastOpenCardModalTime : Infinity;
            if (timeSinceOpenCardModal > 1500) {
              lastOpenCardModalTime = null;
              emit('mapChanged', payload);
            }
          }, 600);
        } catch (error) {
          logger.error('Error in map idle handler', error);
          emit('mapError', error instanceof Error ? error : new Error(String(error)));
        }
      }, 300);
    });
    await addMarkersToMap();
    emit('mapLoaded');
  } catch (error) {
    logger.error('Failed to initialize map', error);
    emit('mapError', error instanceof Error ? error : new Error(String(error)));
  }
};

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function distanceKm(a: { latitude: number; longitude: number; }, b: { latitude: number; longitude: number; }) {
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
}

function shouldEmitMapChange(payload: { center: { latitude: number; longitude: number; }; radiusKm: number; zoom?: number }): boolean {
  if (!payload?.center) {
    return false;
  }

  const prev = lastMapQuery;
  if (!prev) {
    return true;
  }

  const movedEnough = distanceKm(prev.center, payload.center) > 0.5; // 500m
  const radiusChangedEnough = Math.abs(payload.radiusKm - prev.radiusKm) / Math.max(prev.radiusKm, 1) > 0.2; // 20%
  const zoomChanged = typeof payload.zoom === 'number' && Math.abs((payload.zoom || 0) - (prev.zoom || 0)) >= 1;

  return movedEnough || radiusChangedEnough || zoomChanged;
}

watch(() => props.surpriseBags, async(newBags) => {
  if (newBags && newBags.length > 0) {
    if (!mapInstance) {
      await nextTick();
      await initializeMap();
      return;
    }
    closeOverlay();
    await addMarkersToMap();
  }
}, { deep: true });

watch(() => props.coordinates, (newCoordinates) => {
  if (mapInstance && newCoordinates) {
    mapInstance.setCenter({
      lat: newCoordinates.latitude,
      lng: newCoordinates.longitude,
    });
  }
});
onMounted(async() => {
  try {
    await loadGoogleMaps();
    await nextTick();
    await initializeMap();
  } catch (error) {
    logger.error('Failed to load Google Maps', error);
    emit('mapError', error instanceof Error ? error : new Error(String(error)));
  }
});
</script>

<template>
  <div :class="['map-container', props.fullPage ? 'map-container--full' : '']">
    <div
      ref="mapRef"
      :class="['map-container__map', props.fullPage ? 'map-container__map--full' : '']"
    />

    <Transition name="fade">
      <div
        v-if="selectedBag"
        class="card-overlay"
        @click.stop
      >
        <button
          aria-label="Close overlay"
          class="card-overlay__close"
          @click="closeOverlay"
        >
          ×
        </button>
        <SurpriseBagCard
          :article="selectedBag"
          :static-translations="staticTranslations"
          @item-clicked="handleItemClicked"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;

  @screen tablet-wide {
    width: 696px;
  }

  @screen desk {
    width: 1052px;
  }
}

.map-container__map {
  height: 400px;
  min-width: 100%;
  border-radius: 10px;
}

.map-container__map--full {
  height: 80vh;
}

.map-container--full {
  min-width: 100%;
}

.card-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: rgba(255,255,255,0.9);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);

  :deep(.card) {
    @media (max-width: 768px) {
      width: 100%;
    }
  }
}

.card-overlay__close {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  transition: all 0.2s ease;
}

.card-overlay__close:hover {
  background: #f5f5f5;
  transform: scale(1.1);
}

.custom-marker {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.custom-marker:hover {
  transform: scale(1.2);
}

.fade-enter-active {
  animation: fadeIn 0.2s ease-in;
}

.fade-leave-active {
  animation: fadeOut 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
